/**
 * Κεντρική ουρά αντιστοίχισης ονομάτων πλατφορμών (efood / Wolt / BOX).
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ: efood και Wolt εκδίδουν ΝΕΑ ids προϊόντων σε κάθε αναδημοσίευση
 * μενού, οπότε ο πίνακας αντιστοίχισης με κλειδί το id «σβήνει» μόνος του — οι
 * Κουλουράδες έφτασαν 16.000 γραμμές τον μήνα χωρίς αντιστοίχιση και το Bread
 * Lenorman έκοβε μπύρες 24% με 13%. Το ΟΝΟΜΑ είναι το μόνο σταθερό κλειδί, αλλά
 * θέλει ανθρώπινη έγκριση: μια λάθος αυτόματη επιλογή είναι λάθος ΦΠΑ σε
 * απόδειξη. Εδώ ο όμιλος αποφασίζει ΜΙΑ φορά για όλα τα καταστήματα.
 *
 * Οι γραμμές με ρίσκο ΦΠΑ μπαίνουν ΠΑΝΩ-ΠΑΝΩ επίτηδες: αυτές είναι που κοστίζουν
 * χρήματα (ΦΠΑ 24 που κόβεται 13). Ο αυτοματισμός δεν τις αγγίζει ποτέ μόνος του
 * (docs/platform_name_mapping.md §1 κανόνας 1).
 *
 * Νέα εμφάνιση (14/10/2026): «Πίνακας» (shared/listPage). Η ουρά είναι πίνακας με τα γρήγορα
 * κουμπιά υποψηφίων ΜΕΣΑ στη γραμμή (ένα κλικ = αντιστοίχιση — η καθημερινή δουλειά της ουράς
 * δεν θέλει δεύτερο κλικ), και το κλικ στη γραμμή ανοίγει συρτάρι με ΟΛΑ τα χειριστήρια που ως
 * τότε ζούσαν στη γραμμή: είδος, «ανά επιλογή», στόχος, υποψήφια, επιλογέας, «Αγνόησε»/«Λάθος».
 * Η κατάσταση είναι επιλογέας μίας γραμμής με τα πλήθη, η κάλυψη 7 ημερών συρτάρι, η μαζική
 * αποδοχή ρωτά με το ConfirmModal. Ίδιες κλήσεις, ίδια σώματα, ίδια μηνύματα, ίδιες πύλες canEdit.
 *
 * i18n: ελληνικά inline, ΧΩΡΙΣ useTranslation — όπως ο γειτονικός OrgPlatformCatalogTab
 * (§13.11). Αν μεταφραστεί η μία, μεταφράζονται μαζί.
 */

import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  Ban, Check, CheckCheck, ChevronLeft, ChevronRight, Loader2, Plus, RefreshCw, Search, Trash2,
} from 'lucide-react';
import { organizationAPI } from '../services/api';
import { formatMoney } from '../utils/formatMoney';
import {
  orgPlatformMappingsAPI,
  isMappingConflict,
  mappingErrorText,
  fixedComponentId,
  parseFixedComponents,
  rowTargetKind,
  MAX_COMPONENT_QUANTITY,
  MAX_FIXED_COMPONENTS,
  MappingCandidate,
  MappingCoverage,
  MappingCounts,
  MappingFixedComponent,
  MappingStatus,
  MappingTargetKind,
  MappingUpdateBody,
  NameMappingRow,
  OrgMappingListParams,
  PlatformName,
} from '../services/platformMappingApi';
import ConfirmModal from './shared/ConfirmModal';
import { SheetField } from './settings/sheet';
import {
  ChipTone, Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, KeyValue,
  KeyValueItem, ListPage, ListToolbar, PRIMARY_BTN, SELECT_CLS, StatusChip,
} from './shared/listPage';
import { Banner, BannerTone, COMPACT_INPUT, GroupTitle, LINK_BTN, ROW_DANGER_BTN, chipClass } from './orgPages/ui';

interface Props {
  orgId: number;
  /**
   * org_owner / org_admin. Η ΛΕΙΤΟΥΡΓΙΑ δεν ρυθμίζεται πια από εδώ (το feature
   * είναι πάντα ενεργό), οπότε η οθόνη δεν το χρησιμοποιεί — μένει στα props
   * γιατί ο γονέας (OrgDashboard) το στέλνει.
   */
  canManage?: boolean;
  /** canManage ή org_editor — αποφασίζουν για τις γραμμές (§8: writes = org_editor). */
  canEdit: boolean;
}

interface CatalogProduct {
  id: number;
  name: string;
  vat: number | null;
}

type PlatformFilter = PlatformName | 'all';
type StatusFilter = MappingStatus | 'all';

/**
 * Μήνυμα της σελίδας. `rowId`: το μήνυμα βγήκε από απόφαση ΜΙΑΣ γραμμής — όσο το συρτάρι της
 * είναι ανοιχτό φαίνεται ΜΕΣΑ του (εκεί κοιτά ο χρήστης· η σελίδα από πίσω είναι σκιασμένη).
 * 'warn' = 409: ούτε επιτυχία ούτε σφάλμα του χρήστη (τρέχει ήδη πέρασμα ή λάθος server).
 */
interface Notice {
  kind: 'ok' | 'err' | 'warn';
  text: string;
  rowId?: number;
}

const NOTICE_TONE: Record<Notice['kind'], BannerTone> = { ok: 'ok', warn: 'warn', err: 'danger' };

/** Πόσες γραμμές ανά σελίδα. Η ουρά μπορεί να έχει χιλιάδες ονόματα. */
const PAGE_SIZE = 100;

/** Ο ΦΠΑ που χρεώνεται σήμερα σε γραμμή χωρίς αντιστοίχιση (backend: PLATFORM_DEFAULT_VAT). */
const DEFAULT_VAT = 13;

const PLATFORMS: PlatformName[] = ['efood', 'wolt', 'box'];

const PLATFORM_LABELS: Record<PlatformName, string> = {
  efood: 'efood',
  wolt: 'Wolt',
  box: 'BOX',
};

const platformLabel = (name: string): string => PLATFORM_LABELS[name as PlatformName] || name;

const STATUS_ORDER: MappingStatus[] = ['pending', 'mapped', 'ignored', 'rejected', 'orphaned'];

const STATUS_LABELS: Record<MappingStatus, string> = {
  pending: 'Εκκρεμεί',
  mapped: 'Αντιστοιχισμένο',
  ignored: 'Αγνοημένο',
  rejected: 'Λάθος',
  orphaned: 'Χάθηκε ο στόχος',
};

/** Χρώμα μόνο για νόημα: εκκρεμές/χαμένο = προσοχή, αντιστοιχισμένο = εντάξει, λάθος = κόκκινο. */
const STATUS_TONES: Record<MappingStatus, ChipTone> = {
  pending: 'warn',
  mapped: 'ok',
  ignored: 'muted',
  rejected: 'danger',
  orphaned: 'warn',
};

const SOURCE_LABELS: Record<string, string> = {
  exact: 'ταυτόσημο όνομα',
  history: 'από ιστορικό',
  manual: 'χειροκίνητα',
};

/**
 * ΦΑΣΗ 2α (spec §12.6): τι ΕΙΝΑΙ η γραμμή.
 *
 * ΓΙΑΤΙ χρειάζεται: το Wolt δημοσιεύει «θέσεις» και όχι προϊόντα — «Χυμός»,
 * «Αναψυκτικά 0.33 l», «Shots 60 ml». Το πραγματικό είδος το λέει η ΕΠΙΛΟΓΗ του
 * πελάτη. Αν δέσουμε το «Χυμός» σε ένα προϊόν, όλοι οι χυμοί χρεώνονται ως
 * αυτός ο ένας (λάθος είδος, συχνά και λάθος ΦΠΑ).
 */
const KIND_OPTIONS: Array<{ value: MappingTargetKind; label: string }> = [
  { value: 'product', label: 'Προϊόν' },
  { value: 'by_option', label: 'Ανά επιλογή' },
  { value: 'ignore', label: 'Αγνόηση' },
];

const KIND_LABELS: Record<string, string> = {
  product: 'Προϊόν',
  by_option: 'Ανά επιλογή',
  ignore: 'Αγνόηση',
  bundle: 'Πακέτο',
};

const KIND_HELP: Record<string, string> = {
  product: 'Το όνομα της πλατφόρμας είναι ΕΝΑ συγκεκριμένο προϊόν του καταλόγου.',
  by_option: 'Το προϊόν το ορίζει η επιλογή του πελάτη (π.χ. «Χυμός» → «Life πορτοκάλι 0.4 l»). '
    + 'Το προϊόν εδώ κάτω χρησιμοποιείται μόνο αν δεν ταιριάξει καμία επιλογή.',
  ignore: 'Δεν είναι προϊόν μας (σχόλια, χρεώσεις) — να μην ξαναεμφανιστεί.',
};

/**
 * ΦΑΣΗ 5 (§13.19.Γ) — «είναι αυτό το όνομα ΜΕΣΑ στο μενού efood;»
 *
 * Τα δύο πεδία τα γράφει ο εισαγωγέας του καταλόγου για ΚΑΘΕ όνομα που βρίσκει
 * στο `platform_catalog_items`, αντιστοιχισμένο ή όχι (migration 228:
 * `in_platform_catalog`, `catalog_store_count`). Διαβάζονται ΤΟΠΙΚΑ και ως
 * προαιρετικά — ο τύπος `NameMappingRow` ανήκει στη φάση 1 και δεν τον αγγίζει
 * αυτό το build (§13.19.ΙΒ: το pc-ui5 πειράζει από αυτή την καρτέλα ΜΟΝΟ το
 * τσιπ και το φίλτρο).
 *
 * ΓΙΑΤΙ ΕΧΕΙ ΣΗΜΑΣΙΑ: η ουρά έχει 41 αναπάντητα ονόματα σε 852 γραμμές. Ένα
 * όνομα που υπάρχει στο μενού του efood είναι ΣΙΓΟΥΡΑ πουλήσιμο είδος και θα
 * ξαναέρθει· ένα που δεν υπάρχει μπορεί να είναι παλιά παραγγελία ή σχόλιο. Η
 * σειρά δουλειάς δεν είναι η ίδια.
 */
interface CatalogPresence {
  in_platform_catalog?: boolean;
  catalog_store_count?: number;
}

const catalogPresence = (row: NameMappingRow): CatalogPresence => row as CatalogPresence;

// Οι ποσότητες του επιλογέα φτιάχνονται μία φορά με απλό βρόχο: το build
// στοχεύει es5 και δεν γίνεται polyfill (ίδιος λόγος με το isFinite παρακάτω).
const QUANTITY_OPTIONS: number[] = [];
for (let n = 1; n <= MAX_COMPONENT_QUANTITY; n++) QUANTITY_OPTIONS.push(n);

// Το εύρος των combining marks (U+0300-U+036F) χτίζεται με fromCharCode ώστε να
// ΜΗΝ γραφτούν αόρατοι χαρακτήρες μέσα στον πηγαίο κώδικα: σε literal regex δεν
// φαίνονται, δεν διαβάζονται σε review και χάνονται στο πρώτο copy-paste (ίδιο
// μοτίβο με το backend/utils/platformNameNormalizer.js).
const COMBINING_MARKS_RE = new RegExp(
  '[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']', 'g'
);

/**
 * Ελαφριά κανονικοποίηση ΜΟΝΟ για την αναζήτηση στον picker: ο χρήστης γράφει
 * «νερο» και περιμένει να βρει το «Νερό». Δεν είναι ο normalizer του backend —
 * εδώ δεν παίρνεται καμία απόφαση, απλώς φιλτράρεται μια λίστα στην οθόνη.
 */
const foldForSearch = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING_MARKS_RE, '')
    .replace(/ς/g, 'σ');

/** Ποσοστό κάλυψης· αν το backend δεν έστειλε pct, υπολογίζεται από τις γραμμές. */
const coveragePct = (c: MappingCoverage): number => {
  const pct = Number(c.pct);
  // Κενό/NaN pct (π.χ. κατάστημα χωρίς γραμμές) → υπολογίζεται εδώ.
  if (isFinite(pct) && pct >= 0 && pct <= 100) return Math.round(pct);
  const lines = Number(c.lines_7d) || 0;
  const mapped = Number(c.mapped_7d) || 0;
  return lines > 0 ? Math.round((mapped / lines) * 100) : 0;
};

type CoverageTone = 'ok' | 'warn' | 'danger';

/** Ίδια όρια με πριν: ≥90 εντάξει, ≥68 προσοχή, αλλιώς κόκκινο. */
const coverageTone = (pct: number): CoverageTone => {
  if (pct >= 90) return 'ok';
  if (pct >= 68) return 'warn';
  return 'danger';
};

/** Η λεπτή μπάρα μένει (δείχνει με μια ματιά ποιο κατάστημα υστερεί), σε απαλό χρώμα. */
const COVERAGE_BAR: Record<CoverageTone, string> = {
  ok: 'bg-emerald-500/50',
  warn: 'bg-amber-500/55',
  danger: 'bg-red-500/50',
};

// isFinite αντί για Number.isNaN: το build στοχεύει es5 και δεν γίνεται polyfill.
const fmtPrice = (value: number | string | null): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return isFinite(n) ? formatMoney(n) : '—';
};

const fmtDate = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return isFinite(d.getTime())
    ? d.toLocaleDateString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', year: '2-digit' })
    : '—';
};

const linesText = (n: number): string => `${n} ${n === 1 ? 'γραμμή' : 'γραμμές'}`;

/** Το id του κεντρικού προϊόντος ενός υποψηφίου (org εμβέλεια). */
const candidateProductId = (cand: MappingCandidate): number | null => {
  if (cand.org_product_id != null) return Number(cand.org_product_id);
  if (cand.internal_product_id != null) return Number(cand.internal_product_id);
  return null;
};

/** Υπάρχει έστω ένα υποψήφιο που πατιέται; (ένα χωρίς id δεν γίνεται κουμπί) */
const hasCandidates = (row: NameMappingRow): boolean =>
  (row.candidates || []).some(cand => candidateProductId(cand) != null);

/**
 * Αλλαγή που αγγίζει ΜΟΝΟ το σπάσιμο και τα σταθερά συστατικά (spec §12.6).
 *
 * ΓΙΑΤΙ ξεχωρίζει: η κατάσταση, ο στόχος και το είδος της γραμμής μένουν ίδια,
 * άρα ΔΕΝ αλλάζουν ούτε τα πλήθη ανά κατάσταση ούτε η κάλυψη ανά κατάστημα —
 * μια ποσότητα «×2» δεν δικαιολογεί νέο ερώτημα ολόκληρης της λίστας με το join
 * της κάλυψης. Και η γραμμή δεν μπορεί να φύγει από το φίλτρο: το συρτάρι μένει ανοιχτό.
 */
const isComponentOnlyBody = (body: MappingUpdateBody): boolean => {
  const keys = Object.keys(body || {});
  return keys.length > 0 && keys.every(k => k === 'fixed_components' || k === 'explode_components');
};

/**
 * Η γραμμή που γυρίζει το PUT — ίδια μορφή με το GET (routes: shapeRow).
 *
 * ΓΙΑΤΙ ανέχεται δύο σχήματα: ο τύπος της υπηρεσίας επιτρέπει και `{ row }`, και
 * ένας παλιότερος server μπορεί να μη στέλνει γραμμή καθόλου. Δεχόμαστε ΜΟΝΟ τη
 * γραμμή που ζητήσαμε (ίδιο id)· σε οτιδήποτε άλλο γυρνά null και ο caller
 * ξαναδιαβάζει τη λίστα — ποτέ μισή ή λάθος ενημέρωση στην οθόνη.
 */
const rowFromUpdate = (data: any, expectedId: number): NameMappingRow | null => {
  const row = data && typeof data === 'object'
    ? (data.row && typeof data.row === 'object' ? data.row : data)
    : null;
  return row && Number(row.id) === Number(expectedId) ? (row as NameMappingRow) : null;
};

/* ───────────────────────────── κομμάτια της γραμμής ───────────────────────────── */

/**
 * Η κατάσταση μιας γραμμής. Μια αποφασισμένη γραμμή «ανά επιλογή» ΔΕΝ διαβάζεται ως
 * «Αντιστοιχισμένο»: δεν δείχνει σε προϊόν, δείχνει στην επιλογή του πελάτη — και συχνά
 * δεν έχει καθόλου στόχο.
 */
const statusChip = (row: NameMappingRow): React.ReactElement => {
  if (rowTargetKind(row) === 'by_option' && row.status === 'mapped') {
    return <StatusChip tone="brand">{KIND_LABELS.by_option}</StatusChip>;
  }
  return <StatusChip tone={STATUS_TONES[row.status] || 'muted'}>{STATUS_LABELS[row.status] || row.status}</StatusChip>;
};

/** Ετικέτες δίπλα στο όνομα: ρίσκο ΦΠΑ, πακέτο, επιλογή πελάτη, «στο μενού efood». */
const flagChips = (row: NameMappingRow): React.ReactElement[] => {
  const out: React.ReactElement[] = [];
  if (row.vat_risk) {
    out.push(
      <StatusChip
        key="vat"
        tone="danger"
        title={`Κάποιο υποψήφιο προϊόν έχει ΦΠΑ διαφορετικό από ${DEFAULT_VAT}% — χρειάζεται ανθρώπινη απόφαση`}
      >
        ΦΠΑ
      </StatusChip>
    );
  }
  if (row.is_bundle_hint) {
    out.push(
      <StatusChip key="bundle" tone="muted" title="Μοιάζει με πακέτο πολλών προϊόντων — δεν αντιστοιχίζεται σε ένα προϊόν">
        πακέτο
      </StatusChip>
    );
  }
  // Το όνομα ήρθε από ΕΠΙΛΟΓΗ πελάτη: χωρίς τον γονέα, το «life πορτοκάλι 0.4 l» στην ουρά δεν
  // λέει τίποτα. Το κείμενο κόβεται με «…» ώστε ένας μακρύς γονέας να μη σπρώχνει τη στήλη.
  if (row.seen_as_option) {
    out.push(
      <StatusChip
        key="option"
        tone="brand"
        title={row.option_context
          ? `Εμφανίστηκε ως επιλογή στα: ${row.option_context}`
          : 'Το όνομα μαζεύτηκε από επιλογή πελάτη'}
      >
        <span className="block max-w-[11rem] truncate">
          {row.option_context ? `επιλογή του: ${row.option_context}` : 'επιλογή'}
        </span>
      </StatusChip>
    );
  }
  // «Στο μενού efood (N)» — Φ5, §13.19.Γ. Το N είναι ΚΑΤΑΣΤΗΜΑΤΑ, όχι παραγγελίες: ένα όνομα σε
  // 20/20 καταστήματα είναι είδος της αλυσίδας και αξίζει απόφαση σήμερα· ένα σε 1/20 μπορεί να
  // είναι τοπικό. Ο μετρητής μπαίνει μόνο όταν τον στέλνει ο server — «(0)» θα διαβαζόταν ως «σε
  // κανένα κατάστημα».
  const presence = catalogPresence(row);
  if (presence.in_platform_catalog) {
    const stores = presence.catalog_store_count;
    out.push(
      <StatusChip
        key="catalog"
        tone="ok"
        title={'Αυτό το όνομα υπάρχει στον εισαγμένο κατάλογο του efood.\n'
          + 'Δηλαδή είναι πουλήσιμο είδος και θα ξαναέρθει σε παραγγελία — '
          + 'η απόφαση εδώ δεν περιμένει.'}
      >
        στο μενού efood{typeof stores === 'number' && stores > 0 ? ` (${stores})` : ''}
      </StatusChip>
    );
  }
  return out;
};

/** Η κατάσταση με τις μικρές γραμμές της (σπάσιμο, πηγή, ποιος αποφάσισε). */
const StatusCell: React.FC<{ row: NameMappingRow }> = ({ row }) => {
  const byOption = rowTargetKind(row) === 'by_option';
  return (
    <div className="space-y-1">
      {statusChip(row)}
      {byOption && row.explode_components === true && (
        <div className="text-[11px] text-brand">σπάσιμο σε συστατικά</div>
      )}
      {/* Το σπάσιμο το διαβάζει ο resolver ΜΟΝΟ σε γραμμές «Ανά επιλογή». Η ρύθμιση επιβιώνει
          σκόπιμα ενός προσωρινού «Προϊόν» (routes/orgPlatformMappings.js:978), αλλά τότε δεν κάνει
          τίποτα — και χωρίς αυτή την ένδειξη φαίνεται ότι το combo σπάει ενώ στην πραγματικότητα
          μένει ολόκληρο (Κουλουράδες, 16/9/2026). */}
      {!byOption && row.explode_components === true && (
        <div className="text-[11px] text-amber-700 dark:text-amber-300">
          σπάσιμο ανενεργό — θέλει είδος «Ανά επιλογή»
        </div>
      )}
      {row.match_source && (
        <div className="text-[11px] text-t4">{SOURCE_LABELS[row.match_source] || row.match_source}</div>
      )}
      {row.reviewed_by && (
        <div className="text-[11px] text-t4">{row.reviewed_by} · {fmtDate(row.reviewed_at)}</div>
      )}
    </div>
  );
};

/** Ο στόχος της γραμμής με τον ΦΠΑ του (πορτοκαλί όταν δεν είναι ο 13% που χρεώνεται σήμερα). */
const TargetLine: React.FC<{ row: NameMappingRow }> = ({ row }) => {
  const targetName = row.target ? (row.target.name || null) : null;
  const targetVat = row.target && row.target.vat != null ? Number(row.target.vat) : null;
  if (!targetName) return <span className="text-t4">—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="text-t1">{targetName}</span>
      {targetVat != null && (
        <StatusChip tone={targetVat === DEFAULT_VAT ? 'muted' : 'warn'}>ΦΠΑ {targetVat}%</StatusChip>
      )}
    </span>
  );
};

const CAND_SAFE = 'border-line-strong bg-surface-2 text-t2 hover:bg-surface-3 hover:text-t1';
const CAND_RISKY = 'border-red-500/40 bg-red-500/10 text-red-700 hover:bg-red-500/15 dark:text-red-300';

/**
 * Υποψήφια: ένα κλικ = εφαρμογή. Τα fuzzy είναι ΜΟΝΟ προτάσεις (spec §1 κανόνας 2), γι’ αυτό δεν
 * εφαρμόζονται ποτέ μόνα τους. Ολόκληρο το όνομα, μία πρόταση ανά γραμμή: με δύο παρόμοια
 * «ΚΟΥΛΟΥΡΙ PHILADELPHIA, GOU…» ο χρήστης δεν ξεχώριζε ποιο πατά. Κόκκινο = ΦΠΑ ≠ 13%.
 */
const CandidateButtons: React.FC<{
  row: NameMappingRow;
  busy: boolean;
  /** Στο συρτάρι: μεγαλύτερα, για δάχτυλο. */
  large?: boolean;
  onPick: (productId: number, productName: string) => void;
}> = ({ row, busy, large = false, onPick }) => (
  <div className={`flex flex-col items-start ${large ? 'gap-1.5' : 'gap-1'}`}>
    {(row.candidates || []).map((cand, idx) => {
      const pid = candidateProductId(cand);
      if (pid == null) return null;
      const vat = cand.vat != null ? Number(cand.vat) : null;
      const risky = vat !== DEFAULT_VAT;
      const kindLabel = cand.kind === 'exact' ? 'ίδιο' : cand.kind === 'history' ? 'ιστορικό' : `μοιάζει ${Math.round((Number(cand.score) || 0) * 100)}%`;
      return (
        <button
          key={`${row.id}-${pid}-${idx}`}
          type="button"
          disabled={busy}
          onClick={() => onPick(pid, cand.name)}
          title={`${cand.name} — ${vat != null ? `ΦΠΑ ${vat}%` : 'ΦΠΑ άγνωστο'} — ${kindLabel}. Κλικ = αντιστοίχιση.`}
          className={`flex max-w-full items-start gap-1.5 rounded-lg border text-left transition-colors disabled:opacity-50 ${
            large ? 'min-h-[40px] px-3 py-2 text-sm' : 'px-2 py-1 text-xs'} ${risky ? CAND_RISKY : CAND_SAFE}`}
        >
          <Check className={`shrink-0 ${large ? 'mt-[3px] h-3.5 w-3.5' : 'mt-0.5 h-3 w-3'}`} aria-hidden="true" />
          <span className="whitespace-normal break-words font-medium">{cand.name}</span>
          <span className="shrink-0 whitespace-nowrap opacity-70">{vat != null ? `${vat}%` : 'ΦΠΑ;'}</span>
          <span className="shrink-0 whitespace-nowrap opacity-60">{kindLabel}</span>
        </button>
      );
    })}
  </div>
);

/**
 * Αναζήτηση στον κεντρικό κατάλογο με το ερώτημα ΤΟΠΙΚΑ (δικό του state).
 *
 * ΓΙΑΤΙ ξεχωριστό component: η οθόνη έχει δύο pickers (στόχος και σταθερά συστατικά). Με ένα
 * κοινό `pickerQ` στον γονέα, ό,τι πληκτρολογούσε ο χρήστης στο ένα κουτί φιλτράριζε και το άλλο.
 */
const CatalogPicker: React.FC<{
  products: CatalogProduct[];
  disabled?: boolean;
  placeholder?: string;
  onPick: (product: CatalogProduct) => void;
}> = ({ products, disabled = false, placeholder = 'Αναζήτηση στον κεντρικό κατάλογο…', onPick }) => {
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const needle = foldForSearch(query);
    const list = needle
      ? products.filter(p => foldForSearch(p.name).indexOf(needle) !== -1)
      : products;
    return list.slice(0, 40);
  }, [products, query]);

  return (
    <div className="w-full max-w-md rounded-xl border border-line-strong p-2" data-testid="mapping-catalog-picker">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-t4" aria-hidden="true" />
        <input
          type="text"
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className={`${COMPACT_INPUT} w-full pl-8`}
        />
      </label>
      <div className="mt-1.5 max-h-56 space-y-0.5 overflow-y-auto">
        {results.length === 0 && (
          <p className="p-2 text-xs text-t4">Δεν βρέθηκε προϊόν.</p>
        )}
        {results.map(p => (
          <button
            key={p.id}
            type="button"
            disabled={disabled}
            onClick={() => onPick(p)}
            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-t2 transition-colors hover:bg-surface-3 hover:text-t1 disabled:opacity-50"
          >
            <span className="whitespace-normal break-words" title={p.name}>{p.name}</span>
            <span className={`ml-auto shrink-0 text-[11px] ${
              p.vat != null && p.vat !== DEFAULT_VAT
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-t4'}`}>
              {p.vat != null ? `ΦΠΑ ${p.vat}%` : ''}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Οι ρυθμίσεις μιας γραμμής «Ανά επιλογή» (spec §12.6).
 *
 * ΓΙΑΤΙ το σπάσιμο είναι ΚΛΕΙΣΤΟ από προεπιλογή και με προειδοποίηση: η απόδειξη
 * παύει να έχει μία γραμμή «combo» και αποκτά N γραμμές συστατικών με τιμές που
 * ΠΡΟΚΥΠΤΟΥΝ από τη μοιρασιά του συνόλου. Το άθροισμα μένει ίδιο (ο resolver
 * ρίχνει το υπόλοιπο στο τελευταίο συστατικό), αλλά αλλάζει η ΕΙΚΟΝΑ του
 * παραστατικού και οι συντελεστές ΦΠΑ ανά είδος — το αποφασίζει άνθρωπος.
 */
const ByOptionPanel: React.FC<{
  row: NameMappingRow;
  products: CatalogProduct[];
  productsById: Map<number, CatalogProduct>;
  canEdit: boolean;
  busy: boolean;
  onChange: (row: NameMappingRow, body: MappingUpdateBody, okText: string) => void;
}> = ({ row, products, productsById, canEdit, busy, onChange }) => {
  const [adding, setAdding] = useState(false);
  const components = parseFixedComponents(row.fixed_components);
  const explode = row.explode_components === true;
  const full = components.length >= MAX_FIXED_COMPONENTS;

  const saveComponents = (next: MappingFixedComponent[], okText: string) => {
    setAdding(false);
    onChange(row, { fixed_components: next }, okText);
  };

  const addComponent = (product: CatalogProduct) => {
    if (full) return;
    // Δεύτερη φορά το ίδιο προϊόν = μία γραμμή με μεγαλύτερη ποσότητα (έτσι το
    // ενώνει και ο resolver) — όχι δύο ίδιες γραμμές στην απόδειξη.
    const existing = components.findIndex(c => fixedComponentId(c) === product.id);
    if (existing !== -1) {
      const next = components.map((c, i) => (
        i === existing
          ? { ...c, quantity: Math.min((Number(c.quantity) || 1) + 1, MAX_COMPONENT_QUANTITY) }
          : c
      ));
      saveComponents(next, `Η ποσότητα του «${product.name}» αυξήθηκε.`);
      return;
    }
    saveComponents(
      components.concat([{ org_product_id: product.id, quantity: 1 }]),
      `Προστέθηκε σταθερό συστατικό: «${product.name}».`
    );
  };

  const changeQuantity = (index: number, quantity: number) =>
    saveComponents(
      components.map((c, i) => (i === index ? { ...c, quantity } : c)),
      'Η ποσότητα του συστατικού αποθηκεύτηκε.'
    );

  const removeComponent = (index: number, name: string) =>
    saveComponents(
      components.filter((_, i) => i !== index),
      `Αφαιρέθηκε το σταθερό συστατικό «${name}».`
    );

  return (
    <div className="space-y-4 rounded-xl border border-line p-3.5" data-testid="mapping-by-option">
      <label className={`flex items-start gap-2.5 text-sm ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}>
        <input
          type="checkbox"
          checked={explode}
          disabled={!canEdit || busy}
          onChange={e => onChange(
            row,
            { explode_components: e.target.checked },
            e.target.checked
              ? `Το «${row.display_name}» θα σπάει σε συστατικά.`
              : `Το «${row.display_name}» δεν σπάει πια σε συστατικά.`
          )}
          className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[hsl(var(--brand))]"
        />
        <span className="min-w-0">
          <span className="font-medium text-t1">Σπάσιμο σε συστατικά (combo)</span>
          <span className="mt-0.5 block text-xs text-amber-700 dark:text-amber-300">
            Προσοχή: η απόδειξη θα δείχνει τα συστατικά, με τιμές που προκύπτουν από τη μοιρασιά
            του συνόλου της γραμμής.
          </span>
        </span>
      </label>

      {/* Σταθερά συστατικά: ό,τι μπαίνει ΠΑΝΤΑ στο σπάσιμο και δεν υπάρχει σε
          καμία επιλογή του πελάτη (π.χ. το νερό του combo πρωινού). Φαίνονται
          και με κλειστό σπάσιμο — ετοιμάζονται πριν ανοίξει ο διακόπτης — αλλά
          το λέμε ρητά ότι χωρίς σπάσιμο δεν κάνουν τίποτα. */}
      <div>
        <GroupTitle>Σταθερά συστατικά ({components.length})</GroupTitle>
        {!explode && components.length > 0 && (
          <p className="mt-1 text-xs text-t4">
            Ισχύουν μόνο όταν είναι ανοιχτό το σπάσιμο σε συστατικά.
          </p>
        )}

        {components.length === 0 ? (
          <p className="mt-1 text-xs text-t4">
            Κανένα. Μπαίνουν μόνο τα προϊόντα που ΔΕΝ είναι επιλογή του πελάτη.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-line">
            {components.map((component, index) => {
              const id = fixedComponentId(component);
              const product = id != null ? productsById.get(id) : undefined;
              // Πρώτα το όνομα που στέλνει το backend (το διαβάζει με την ίδια
              // εμβέλεια που θα χρεώσει), μετά ο κατάλογος της οθόνης. Χωρίς
              // κανένα από τα δύο το συστατικό ΔΕΝ υπάρχει πια — το λέμε ρητά,
              // γιατί ο resolver θα αρνηθεί να σπάσει το combo.
              const missing = !component.name && !product;
              const name = component.name || product?.name || (id != null ? `#${id}` : '—');
              return (
                <li key={`${row.id}-fixed-${id ?? index}`} className="flex items-center gap-2 py-1.5 text-sm">
                  <span
                    className={`min-w-0 flex-1 truncate ${missing ? 'text-red-700 dark:text-red-400' : 'text-t2'}`}
                    title={missing ? `${name} — δεν υπάρχει στον κατάλογο` : name}
                  >
                    {name}{missing ? ' · λείπει' : ''}
                  </span>
                  {canEdit ? (
                    <select
                      value={component.quantity}
                      disabled={busy}
                      onChange={e => changeQuantity(index, Number(e.target.value))}
                      aria-label={`Ποσότητα για ${name}`}
                      className={`${COMPACT_INPUT} w-[4.75rem] shrink-0 tabular-nums`}
                    >
                      {QUANTITY_OPTIONS.map(n => (
                        <option key={n} value={n}>×{n}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="shrink-0 tabular-nums text-t3">×{component.quantity}</span>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => removeComponent(index, name)}
                      title="Αφαίρεση συστατικού"
                      aria-label="Αφαίρεση συστατικού"
                      className={ROW_DANGER_BTN}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {canEdit && !full && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setAdding(!adding)}
            className={`${LINK_BTN} mt-2`}
          >
            <Plus className="h-4 w-4" aria-hidden="true" /> {adding ? 'Κλείσιμο' : 'Προσθήκη σταθερού συστατικού…'}
          </button>
        )}
        {canEdit && full && (
          <p className="mt-2 text-xs text-t4">Έφτασες το όριο των {MAX_FIXED_COMPONENTS} συστατικών.</p>
        )}
        {canEdit && adding && !full && (
          <div className="mt-2">
            <CatalogPicker products={products} disabled={busy} onPick={addComponent} />
          </div>
        )}
      </div>
    </div>
  );
};

/** Κάλυψη ενός καταστήματος σε μία πλατφόρμα: ποσοστό, λεπτή μπάρα, «N από M γραμμές». */
const CoverageCell: React.FC<{ item: MappingCoverage }> = ({ item }) => {
  const pct = coveragePct(item);
  const tone = coverageTone(pct);
  const lines = Number(item.lines_7d) || 0;
  return (
    <div className="space-y-1.5">
      <StatusChip tone={tone}>{pct}%</StatusChip>
      <div className="h-1 max-w-[9rem] overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
        <div className={`h-full rounded-full ${COVERAGE_BAR[tone]}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
      <div className="text-[11px] tabular-nums text-t4">
        {Number(item.mapped_7d) || 0} από {lines} γραμμές
      </div>
    </div>
  );
};

interface CoverageGroup {
  storeId: number;
  storeName: string;
  items: MappingCoverage[];
}

/* ───────────────────────────── η σελίδα ───────────────────────────── */

const OrgPlatformMappingsTab: React.FC<Props> = ({ orgId, canEdit }) => {
  const uid = useId();
  const kindSelectId = `${uid}-kind`;
  const [rows, setRows] = useState<NameMappingRow[]>([]);
  const [counts, setCounts] = useState<MappingCounts | null>(null);
  const [coverage, setCoverage] = useState<MappingCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState<'suggest' | 'accept' | null>(null);
  // 'warn' = 409: ούτε επιτυχία ούτε σφάλμα του χρήστη (τρέχει ήδη πέρασμα ή
  // λάθος server) — μένει στην οθόνη με το ελληνικό κείμενο του backend.
  const [notice, setNotice] = useState<Notice | null>(null);

  // Φίλτρα (πάνε στο backend) — η αναζήτηση με μικρή καθυστέρηση ώστε να μην
  // χτυπάει ερώτημα σε κάθε πλήκτρο.
  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [vatOnly, setVatOnly] = useState(false);
  // Φάση 2α: τα ονόματα που μαζεύτηκαν από ΕΠΙΛΟΓΕΣ πελατών (seen_as_option).
  // Είναι άλλη δουλειά από τα προϊόντα: δουλεύονται μαζικά ανά γονικό «Χυμός».
  const [optionsOnly, setOptionsOnly] = useState(false);
  // Φάση 5 (§13.19.Γ): μόνο ονόματα που υπάρχουν ΣΗΜΕΡΑ στο μενού του efood.
  const [inCatalogOnly, setInCatalogOnly] = useState(false);
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [offset, setOffset] = useState(0);

  // Picker στόχου: ανοίγει για ΜΙΑ γραμμή τη φορά (η αναζήτηση ζει μέσα του).
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [pickerRowId, setPickerRowId] = useState<number | null>(null);

  // Η κάλυψη 7 ημερών είναι έλεγχος, όχι καθημερινή δουλειά: μπαίνει σε συρτάρι
  // ώστε η ουρά (που ΕΙΝΑΙ η δουλειά) να πιάνει όλη την οθόνη.
  const [coverageOpen, setCoverageOpen] = useState(false);

  // Η γραμμή του συρταριού, με το id της: μετά από αποθήκευση το συρτάρι δείχνει τη ΦΡΕΣΚΙΑ
  // γραμμή της λίστας, όχι ένα αντίγραφο της στιγμής που άνοιξε.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Η μαζική αποδοχή ρωτά πρώτα (ήταν window.confirm).
  const [confirmAccept, setConfirmAccept] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setQApplied(q.trim()), 350);
    return () => clearTimeout(handle);
  }, [q]);

  // Αλλαγή φίλτρου ⇒ πίσω στην πρώτη σελίδα, αλλιώς βλέπεις «κενό» επειδή το
  // offset δείχνει πέρα από το νέο σύνολο.
  useEffect(() => {
    setOffset(0);
  }, [platform, status, vatOnly, optionsOnly, inCatalogOnly, qApplied]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: OrgMappingListParams = { limit: PAGE_SIZE, offset };
      if (platform !== 'all') params.platform = platform;
      if (status !== 'all') params.status = status;
      if (vatOnly) params.vat_risk = 1;
      if (optionsOnly) params.only_options = 1;
      // Το `OrgMappingListParams` ανήκει στη φάση 1 και δεν το αγγίζει αυτό το
      // build· το κλειδί μπαίνει τοπικά, με την ΙΔΙΑ σύμβαση 1/0 που ήδη
      // χρησιμοποιούν τα δύο παραπάνω. Server χωρίς τη φάση 5 το αγνοεί — και
      // τότε το τσιπ δεν εμφανίζεται πουθενά, οπότε δεν υπόσχεται τίποτα.
      if (inCatalogOnly) (params as Record<string, unknown>).in_catalog = 1;
      if (qApplied) params.q = qApplied;
      const data = await orgPlatformMappingsAPI.list(orgId, params);
      setRows(data?.rows || []);
      setCounts(data?.counts || null);
      setCoverage(data?.coverage || []);
    } catch (err: any) {
      setNotice({
        kind: 'err',
        text: err?.response?.data?.error || 'Αποτυχία φόρτωσης των αντιστοιχίσεων',
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, platform, status, vatOnly, optionsOnly, inCatalogOnly, qApplied, offset]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    organizationAPI.getCatalogProducts(orgId)
      .then((r: any) => setProducts((r.products || r || []).map((x: any) => ({
        id: x.id,
        name: x.name,
        vat: x.vat != null ? Number(x.vat) : null,
      }))))
      .catch(() => setProducts([]));
  }, [orgId]);

  // Η γραμμή του συρταριού έφυγε από τη λίστα (απόφαση που τη βγάζει από το φίλτρο, άλλη
  // σελίδα): το συρτάρι κλείνει, και το id δεν μένει πίσω να το ξανανοίξει μόνο του αργότερα.
  useEffect(() => {
    if (selectedId !== null && !rows.some(r => r.id === selectedId)) {
      setSelectedId(null);
      setPickerRowId(null);
    }
  }, [rows, selectedId]);

  // Οι γραμμές με ρίσκο ΦΠΑ πρώτες, μετά κατά συχνότητα: ο χρόνος του ανθρώπου
  // πιάνει τόπο εκεί που χάνονται χρήματα και εκεί που περνούν πολλές γραμμές.
  const sortedRows = useMemo(() => {
    const list = rows.slice();
    list.sort((a, b) => {
      if (!!a.vat_risk !== !!b.vat_risk) return a.vat_risk ? -1 : 1;
      return (Number(b.seen_count) || 0) - (Number(a.seen_count) || 0);
    });
    return list;
  }, [rows]);

  // Κάλυψη ανά κατάστημα (μία γραμμή) με μία στήλη ανά πλατφόρμα.
  const coverageByStore = useMemo<CoverageGroup[]>(() => {
    const order: number[] = [];
    const bucket: Record<string, CoverageGroup> = {};
    for (const c of coverage) {
      const id = Number(c.store_id);
      const key = String(id);
      if (!bucket[key]) {
        bucket[key] = { storeId: id, storeName: c.store_name || `Κατάστημα #${id}`, items: [] };
        order.push(id);
      }
      bucket[key].items.push(c);
    }
    return order.map(id => bucket[String(id)]);
  }, [coverage]);

  // Οι πλατφόρμες που έχουν στοιχεία κάλυψης, στη σειρά efood → Wolt → BOX (και ό,τι άλλο
  // στείλει ο server, στο τέλος — όχι να χαθεί σιωπηλά).
  const coveragePlatforms = useMemo(() => {
    const out: string[] = [];
    PLATFORMS.forEach(p => { if (coverage.some(c => c.platform_name === p)) out.push(p); });
    coverage.forEach(c => { if (out.indexOf(c.platform_name) === -1) out.push(c.platform_name); });
    return out;
  }, [coverage]);

  // Τα σταθερά συστατικά αποθηκεύονται ως ids: το όνομα για την οθόνη βγαίνει
  // από τον κατάλογο που έχει ήδη κατέβει, χωρίς δεύτερη κλήση ανά γραμμή.
  const productsById = useMemo(() => {
    const map = new Map<number, CatalogProduct>();
    for (const p of products) map.set(Number(p.id), p);
    return map;
  }, [products]);

  const selectedRow = useMemo(
    () => (selectedId === null ? null : rows.find(r => r.id === selectedId) || null),
    [rows, selectedId]
  );

  const updateRow = async (row: NameMappingRow, body: MappingUpdateBody, okText: string) => {
    if (!canEdit || busyId !== null) return;
    setBusyId(row.id);
    setNotice(null);
    try {
      const saved = await orgPlatformMappingsAPI.update(orgId, row.id, body);
      setPickerRowId(null);
      // Αλλαγή μόνο σε συστατικά/σπάσιμο: γράφουμε ΤΗ ΓΡΑΜΜΗ που απάντησε το PUT
      // (ίδια μορφή με το GET, με τα ονόματα των συστατικών μέσα) αντί να
      // ξαναδιαβάσουμε όλη τη λίστα για μία ποσότητα. Αν ο server δεν έστειλε
      // γραμμή, πέφτουμε στη φόρτωση — δεν αφήνουμε την οθόνη με παλιά τιμή.
      const componentOnly = isComponentOnlyBody(body);
      const fresh = componentOnly ? rowFromUpdate(saved, row.id) : null;
      if (fresh) {
        setRows(prev => prev.map(r => (r.id === row.id ? { ...r, ...fresh } : r)));
      } else {
        await load();
      }
      // Απόφαση που ξαναφορτώνει τη λίστα (η γραμμή μπορεί να φύγει από το φίλτρο) κλείνει
      // το συρτάρι· αλλαγή μόνο σε συστατικά/σπάσιμο το αφήνει ανοιχτό, με τη φρέσκια γραμμή.
      if (!componentOnly) setSelectedId(null);
      setNotice({ kind: 'ok', text: okText, rowId: row.id });
    } catch (err: any) {
      setNotice({ kind: 'err', text: mappingErrorText(err) || 'Αποτυχία αποθήκευσης', rowId: row.id });
    } finally {
      setBusyId(null);
    }
  };

  const applyTarget = (row: NameMappingRow, productId: number, productName: string) => {
    const kind = rowTargetKind(row);
    // Σε γραμμή «ανά επιλογή» το προϊόν είναι η ΕΦΕΔΡΕΙΑ, όχι ο στόχος: δεν
    // αλλάζουμε το είδος της γραμμής επειδή ο χρήστης διάλεξε εφεδρικό προϊόν.
    return updateRow(
      row,
      { org_product_id: productId, status: 'mapped' },
      kind === 'by_option'
        ? `Εφεδρικό προϊόν για το «${row.display_name}»: «${productName}».`
        : `«${row.display_name}» → «${productName}».`
    );
  };

  /**
   * Αλλαγή είδους γραμμής (spec §12.6).
   *
   * ΓΙΑΤΙ το «Ανά επιλογή» γράφει ΚΑΙ status 'mapped': ο resolver φορτώνει μόνο
   * γραμμές 'mapped'/'ignored' (loadNameContext). Μια γραμμή by_option που
   * έμενε 'pending' δεν θα έλυνε ΠΟΤΕ καμία επιλογή και ο χρήστης θα έβλεπε τη
   * ρύθμισή του να μην κάνει τίποτα. Ο στόχος είναι προαιρετικός εδώ (§12.1).
   */
  const changeKind = (row: NameMappingRow, kind: MappingTargetKind) => {
    if (kind === rowTargetKind(row)) return;
    if (kind === 'ignore') {
      return updateRow(row, { target_kind: 'ignore', status: 'ignored' },
        `Το «${row.display_name}» αγνοείται από εδώ και πέρα.`);
    }
    if (kind === 'by_option') {
      return updateRow(row, { target_kind: 'by_option', status: 'mapped' },
        `Το «${row.display_name}» θα παίρνει το προϊόν του από την επιλογή του πελάτη.`);
    }
    // Πίσω σε κανονικό προϊόν: χωρίς στόχο η γραμμή δεν είναι αντιστοιχισμένη —
    // γυρίζει στα εκκρεμή για να τη δει άνθρωπος, αλλιώς θα έμενε 'mapped' και
    // θα την προσπερνούσε η ουρά με κενό προϊόν.
    //
    // ΔΕΝ στέλνουμε explode_components/fixed_components: ο resolver τα διαβάζει
    // μόνο σε γραμμές by_option, και το backend επίτηδες ΔΕΝ τα καθαρίζει (ένα
    // προσωρινό «Προϊόν» δεν επιτρέπεται να σβήσει τη ρύθμιση του combo).
    //
    // Ρητό status ΠΑΝΤΑ: γραμμή που ερχόταν από «Αγνόηση» έχει status 'ignored'
    // και το backend ξαναγράφει target_kind 'ignore' όσο η κατάσταση λέει
    // 'ignored' — χωρίς αυτό η αλλαγή είδους δεν θα είχε κανένα αποτέλεσμα.
    const hasTarget = !!row.target && (row.target.org_product_id != null || row.target.internal_product_id != null);
    const body: MappingUpdateBody = { target_kind: 'product', status: hasTarget ? 'mapped' : 'pending' };
    return updateRow(row, body, `Το «${row.display_name}» αντιστοιχίζεται πλέον σε ένα προϊόν.`);
  };

  const ignoreRow = (row: NameMappingRow) =>
    updateRow(row, { status: 'ignored' }, `Το «${row.display_name}» αγνοείται από εδώ και πέρα.`);

  const rejectRow = (row: NameMappingRow) =>
    updateRow(row, { status: 'rejected' }, `Το «${row.display_name}» σημειώθηκε ως λάθος πρόταση.`);

  const runSuggest = async () => {
    if (!canEdit || bulkBusy) return;
    setBulkBusy('suggest');
    setNotice(null);
    try {
      const r: any = await orgPlatformMappingsAPI.suggest(orgId);
      await load();
      setNotice({
        kind: 'ok',
        text: `Έλεγχος ολοκληρώθηκε: ${r?.harvested ?? 0} ονόματα από το ιστορικό, `
          + `${r?.suggested ?? 0} με νέες προτάσεις, ${r?.auto_mapped ?? 0} αντιστοιχίστηκαν αυτόματα.`,
      });
    } catch (err: any) {
      // 409: ή τρέχει ήδη πέρασμα για τον ίδιο όμιλο, ή ο όμιλος δεν ανήκει σε
      // αυτόν τον server. Το backend στέλνει ΣΩΣΤΟ ελληνικό κείμενο — δείχνουμε
      // αυτό, όχι «Αποτυχία ελέγχου» που στέλνει τον χρήστη να ξαναπατήσει.
      setNotice({
        kind: isMappingConflict(err) ? 'warn' : 'err',
        text: mappingErrorText(err) || 'Αποτυχία ελέγχου',
      });
    } finally {
      setBulkBusy(null);
    }
  };

  /** Ρητή επιβεβαίωση: είναι μαζική ενέργεια σε ΟΛΑ τα καταστήματα του ομίλου. */
  const askAcceptAll = () => {
    if (!canEdit || bulkBusy) return;
    setConfirmAccept(true);
  };

  const acceptAllExact = async () => {
    setConfirmAccept(false);
    if (!canEdit || bulkBusy) return;
    setBulkBusy('accept');
    setNotice(null);
    try {
      const r: any = await orgPlatformMappingsAPI.acceptExact(orgId);
      await load();
      setNotice({ kind: 'ok', text: `Εφαρμόστηκαν ${r?.accepted ?? 0} ακριβείς αντιστοιχίσεις.` });
    } catch (err: any) {
      setNotice({
        kind: isMappingConflict(err) ? 'warn' : 'err',
        text: mappingErrorText(err) || 'Αποτυχία μαζικής αποδοχής',
      });
    } finally {
      setBulkBusy(null);
    }
  };

  // Το ερώτημα αναζήτησης ζει ΜΕΣΑ στο CatalogPicker, οπότε καθαρίζει μόνο του
  // όταν κλείνει — δεν χρειάζεται μηδενισμός από εδώ.
  const openPicker = (row: NameMappingRow) => {
    setPickerRowId(pickerRowId === row.id ? null : row.id);
  };

  const openRow = (row: NameMappingRow) => {
    setPickerRowId(null);
    setSelectedId(row.id);
  };

  const closeRow = () => {
    setSelectedId(null);
    setPickerRowId(null);
  };

  const hasPrev = offset > 0;
  const hasNext = rows.length === PAGE_SIZE;

  // ── Πίνακας ──────────────────────────────────────────────────────────────

  const columns: Column<NameMappingRow>[] = [
    {
      key: 'platform',
      header: 'Πλατφόρμα',
      width: '96px',
      render: row => <span className="text-t2">{platformLabel(row.platform_name)}</span>,
      stackRender: row => platformLabel(row.platform_name),
    },
    {
      key: 'name',
      header: 'Όνομα στην πλατφόρμα',
      width: 'minmax(220px, 2.2fr)',
      stack: 'title',
      render: row => (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-t1">{row.display_name}</span>
            {flagChips(row)}
          </div>
          <div className="mt-0.5 truncate font-mono text-[11px] text-t4" title={row.normalized_name}>
            {row.normalized_name}
          </div>
        </div>
      ),
      // Στο κινητό μένει μόνο το όνομα — αλλά το ρίσκο ΦΠΑ (αυτό που κοστίζει χρήματα) ΜΠΡΟΣΤΑ
      // του, ώστε να μην το κόψει το «…» ενός μακριού ονόματος.
      stackRender: row => (row.vat_risk
        ? <><StatusChip tone="danger">ΦΠΑ</StatusChip> {row.display_name}</>
        : row.display_name),
    },
    {
      key: 'seen',
      header: 'Γραμμές',
      width: '88px',
      align: 'right',
      render: row => <span className="text-t2">{Number(row.seen_count) || 0}</span>,
      stackRender: row => linesText(Number(row.seen_count) || 0),
    },
    {
      key: 'price',
      header: 'Τελ. τιμή',
      width: '104px',
      align: 'right',
      hideBelow: 'medium',
      render: row => <span className="text-t3">{fmtPrice(row.last_platform_price)}</span>,
    },
    {
      key: 'status',
      header: 'Κατάσταση',
      width: '168px',
      stack: 'end',
      render: row => <StatusCell row={row} />,
      stackRender: row => statusChip(row),
    },
    {
      key: 'target',
      header: 'Προϊόν καταλόγου',
      width: 'minmax(260px, 2fr)',
      render: row => {
        const kind = rowTargetKind(row);
        const busy = busyId === row.id;
        return (
          <div className="min-w-0 space-y-1.5">
            {/* Ο στόχος μιας γραμμής «ανά επιλογή» είναι ΕΦΕΔΡΕΙΑ. */}
            {kind === 'by_option' && (
              <p className="text-[11px] text-t3">Προϊόν αν δεν ταιριάξει καμία επιλογή</p>
            )}
            {kind !== 'product' && kind !== 'by_option' && (
              <p className="text-[11px] text-t3">Είδος: {KIND_LABELS[kind] || kind}</p>
            )}
            <div className="flex items-center gap-2">
              <TargetLine row={row} />
              {busy && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-t3" aria-hidden="true" />}
            </div>
            {/* Η καθημερινή δουλειά της ουράς: ένα κλικ = αντιστοίχιση, χωρίς να ανοίξει το συρτάρι
                (κλικ σε κουμπί μέσα στη γραμμή δεν την ανοίγει). */}
            {canEdit && hasCandidates(row) && (
              <CandidateButtons row={row} busy={busy} onPick={(pid, name) => applyTarget(row, pid, name)} />
            )}
          </div>
        );
      },
      // Χωρίς στόχο δεν μπαίνει τίποτα στη γραμμή meta (ένα σκέτο «—» εκεί δεν λέει τίποτα).
      stackRender: row => (row.target && row.target.name) || null,
    },
  ];

  const coverageColumns: Column<CoverageGroup>[] = [
    {
      key: 'store',
      header: 'Κατάστημα',
      width: 'minmax(160px, 1.4fr)',
      render: g => <span className="font-medium text-t1">{g.storeName}</span>,
      stackRender: g => g.storeName,
    },
    ...coveragePlatforms.map((p): Column<CoverageGroup> => ({
      key: `platform-${p}`,
      header: platformLabel(p),
      width: 'minmax(120px, 1fr)',
      render: g => {
        const item = g.items.find(i => i.platform_name === p);
        return item ? <CoverageCell item={item} /> : <span className="text-t4">—</span>;
      },
      stackRender: g => {
        const item = g.items.find(i => i.platform_name === p);
        return item ? `${platformLabel(p)} ${coveragePct(item)}%` : null;
      },
    })),
  ];

  // ── Επιλογέας κατάστασης ─────────────────────────────────────────────────
  // ΜΙΑ γραμμή με τα πλήθη: αντικαθιστά το παλιό select ΚΑΙ τα «κουμπιά-πλήθη». Τα πλήθη τα
  // μετρά ο server μέσα στα ίδια φίλτρα (πλατφόρμα, αναζήτηση, τσιπ), χωρίς την κατάσταση.

  const statusOptions: StatusFilter[] = ['all', ...STATUS_ORDER];

  const statusFilter = (
    <div className="lp-seg" role="radiogroup" aria-label="Κατάσταση" data-testid="mapping-status-filter">
      {statusOptions.map(s => {
        const active = status === s;
        const n = s !== 'all' && counts ? (Number(counts[s]) || 0) : null;
        return (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={active}
            // Όπως τα παλιά «κουμπιά-πλήθη»: το πάτημα της ενεργής κατάστασης γυρνά σε «Όλες».
            onClick={() => setStatus(s === 'all' || active ? 'all' : s)}
          >
            {s === 'all' ? 'Όλες' : STATUS_LABELS[s]}
            {n !== null && (
              <>
                {' '}
                <span className="ml-1.5 text-xs font-medium tabular-nums text-t3">{n}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );

  // «41 ονόματα»: το πλήθος της τρέχουσας κατάστασης μέσα στα ίδια φίλτρα (ή όλων, στο «Όλες»).
  const shownTotal = counts
    ? (status === 'all'
      ? STATUS_ORDER.reduce((sum, s) => sum + (Number(counts[s]) || 0), 0)
      : (Number(counts[status]) || 0))
    : null;
  const summaryText = loading && rows.length === 0
    ? null
    : (shownTotal !== null ? `${shownTotal} ${shownTotal === 1 ? 'όνομα' : 'ονόματα'}` : null);

  // Ίδια σελιδοποίηση με πριν: ο server δεν στέλνει σύνολο, άρα «υπάρχουν κι άλλα» = γεμάτη σελίδα.
  const pager = (hasPrev || hasNext) ? (
    <nav className="lp-pager" aria-label="Σελίδες">
      <span className="lp-pager-range">Γραμμές {offset + 1}–{offset + sortedRows.length}</span>
      <span className="lp-pager-nav">
        <button
          type="button"
          className={GHOST_BTN}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          disabled={!hasPrev || loading}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Προηγούμενα
        </button>
        <button
          type="button"
          className={GHOST_BTN}
          onClick={() => setOffset(offset + PAGE_SIZE)}
          disabled={!hasNext || loading}
        >
          Επόμενα <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </span>
    </nav>
  ) : null;

  // ── Συρτάρι γραμμής ──────────────────────────────────────────────────────

  const panelRow = selectedRow;
  const panelKind: MappingTargetKind = panelRow ? rowTargetKind(panelRow) : 'product';
  const panelByOption = panelKind === 'by_option';
  // Το updateRow απορρίπτει ΚΑΘΕ αλλαγή όσο τρέχει αποθήκευση, έστω και σε άλλη γραμμή
  // (busyId !== null). Τα χειριστήρια του πάνελ «ανά επιλογή» κλειδώνουν με ΑΥΤΟ: αλλιώς η
  // δεύτερη αλλαγή έφευγε σιωπηλά και ο επιλογέας γύριζε στην παλιά ποσότητα χωρίς να το
  // καταλάβει ο χρήστης.
  const panelBusy = busyId !== null;
  const rowBusy = panelRow !== null && busyId === panelRow.id;
  const panelNotice = notice && panelRow && notice.rowId === panelRow.id ? notice : null;
  const pageNotice = notice && !panelNotice ? notice : null;
  const panelTargetName = panelRow && panelRow.target ? (panelRow.target.name || null) : null;

  const noticeBanner = (n: Notice, className?: string) => (
    <Banner
      tone={NOTICE_TONE[n.kind]}
      role={n.kind === 'err' ? 'alert' : 'status'}
      onDismiss={() => setNotice(null)}
      dismissLabel="Κλείσιμο μηνύματος"
      className={className}
      testId="mapping-notice"
    >
      {n.text}
    </Banner>
  );

  const panelFlags = panelRow ? flagChips(panelRow) : [];

  const detailItems = (row: NameMappingRow): KeyValueItem[] => {
    const kind = rowTargetKind(row);
    const items: KeyValueItem[] = [
      { id: 'status', label: 'Κατάσταση', value: <StatusCell row={row} /> },
      {
        id: 'normalized',
        label: 'Κανονικοποιημένο όνομα',
        value: <span className="font-mono text-[13px] font-normal">{row.normalized_name}</span>,
      },
    ];
    // Ο θεατής δεν έχει τον επιλογέα «Είδος»: το είδος γράφεται εδώ, όπως πριν στη γραμμή.
    if (!canEdit && kind !== 'product') {
      items.push({ id: 'kind', label: 'Είδος', value: KIND_LABELS[kind] || kind });
    }
    return items;
  };

  return (
    <ListPage
      eyebrow="Πλατφόρμες"
      title="Αντιστοίχιση προϊόντων πλατφορμών"
      subtitle={(
        <>
          Το efood και το Wolt αλλάζουν τους κωδικούς των προϊόντων σε κάθε αναδημοσίευση μενού, γι’ αυτό
          οι παραγγελίες τους καταλήγουν «χωρίς αντιστοίχιση» και χρεώνονται με ΦΠΑ {DEFAULT_VAT}%. Εδώ
          δένεις το <strong className="font-semibold text-t2">όνομα</strong> της πλατφόρμας με το προϊόν του
          κεντρικού καταλόγου — μία φορά για όλα τα καταστήματα.
        </>
      )}
      actions={canEdit ? (
        <button
          type="button"
          className={GHOST_BTN}
          onClick={runSuggest}
          disabled={bulkBusy !== null}
          title="Διαβάζει ξανά το ιστορικό παραγγελιών και υπολογίζει προτάσεις"
        >
          {bulkBusy === 'suggest'
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
          Έλεγχος τώρα
        </button>
      ) : undefined}
      testId="org-platform-mappings"
    >
      {/* Η κατάσταση σε δική της γραμμή, πάνω από τα φίλτρα (στη θέση που έχουν οι υπο-καρτέλες
          του Καταλόγου): είναι η κύρια «προβολή» της ουράς. Μέσα στα filters του ListToolbar, μαζί
          με την πλατφόρμα και τα τρία τσιπ, το μπλοκ δεν χωρούσε δίπλα στην αναζήτηση στα ~1000px
          της σελίδας — η αναζήτηση θα έμενε μόνη σε μια σειρά και τα φίλτρα θα έπιαναν άλλες δύο. */}
      <div className="pb-4">{statusFilter}</div>

      <ListToolbar
        search={{
          value: q,
          onChange: setQ,
          placeholder: 'π.χ. φρέντο',
          ariaLabel: 'Αναζήτηση ονόματος',
          testId: 'mapping-search',
        }}
        filters={(
          <>
            <FilterSelect
              label="Πλατφόρμα"
              value={platform === 'all' ? '' : platform}
              onChange={v => setPlatform(v === '' ? 'all' : (v as PlatformName))}
              options={PLATFORMS.map(p => ({ value: p, label: PLATFORM_LABELS[p] }))}
              allLabel="Όλες οι πλατφόρμες"
              testId="mapping-platform"
            />
            <button type="button" aria-pressed={vatOnly} className={chipClass(vatOnly)} onClick={() => setVatOnly(!vatOnly)}>
              Μόνο με ρίσκο ΦΠΑ
            </button>
            {/* Οι επιλογές («life πορτοκάλι 0.4 l») είναι ξεχωριστή δουλειά από τα
                προϊόντα: μαζεύονται από τα materials των γραμμών «ανά επιλογή». */}
            <button
              type="button"
              aria-pressed={optionsOnly}
              className={chipClass(optionsOnly)}
              onClick={() => setOptionsOnly(!optionsOnly)}
              title="Ονόματα που ήρθαν από επιλογές πελατών, κάτω από γραμμές «ανά επιλογή»"
            >
              Μόνο επιλογές
            </button>
            {/* Φάση 5 (§13.19.Γ): τα ονόματα που ΥΠΑΡΧΟΥΝ στο μενού του efood είναι
                σίγουρα πουλήσιμα είδη και θα ξαναέρθουν — προηγούνται στην ουρά.
                Ένα όνομα εκτός μενού μπορεί να είναι παλιά παραγγελία ή σχόλιο. */}
            <button
              type="button"
              aria-pressed={inCatalogOnly}
              className={chipClass(inCatalogOnly)}
              onClick={() => setInCatalogOnly(!inCatalogOnly)}
              title={'Ονόματα που υπάρχουν ΣΗΜΕΡΑ στον εισαγμένο κατάλογο της πλατφόρμας.\n'
                + 'Δείχνει μόνο όσα έχει δει ο εισαγωγέας καταλόγου — αν δεν έχει τρέξει, η λίστα βγαίνει άδεια.'}
            >
              Μόνο στο μενού efood
            </button>
          </>
        )}
        actions={(
          <>
            <button type="button" className={GHOST_BTN} onClick={() => load()}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Ανανέωση
            </button>
            {/* Η κάλυψη ανοίγει σε συρτάρι: είναι έλεγχος «πώς πάμε», όχι η δουλειά της
                οθόνης. Κλειδωμένο όσο δεν υπάρχουν στοιχεία, ώστε το κουμπί να μην
                πηγαινοέρχεται στη γραμμή σε κάθε φόρτωση. */}
            <button
              type="button"
              className={GHOST_BTN}
              onClick={() => setCoverageOpen(true)}
              disabled={coverageByStore.length === 0}
              title={coverageByStore.length === 0
                ? 'Δεν υπάρχουν ακόμη στοιχεία κάλυψης για τις τελευταίες 7 ημέρες'
                : 'Ποσοστό γραμμών με αντιστοίχιση ανά κατάστημα και πλατφόρμα'}
            >
              Κάλυψη 7 ημερών
            </button>
            {canEdit && (
              <button
                type="button"
                className={PRIMARY_BTN}
                onClick={askAcceptAll}
                disabled={bulkBusy !== null}
                title="Εφαρμόζει μόνο τις γραμμές με ένα ταυτόσημο όνομα και χωρίς ρίσκο ΦΠΑ"
              >
                {bulkBusy === 'accept'
                  ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  : <CheckCheck className="h-4 w-4" aria-hidden="true" />}
                Αποδοχή όλων των ακριβών
              </button>
            )}
          </>
        )}
        summary={summaryText}
      />

      {pageNotice && noticeBanner(pageNotice, 'mb-3')}

      <DataTable
        columns={columns}
        rows={sortedRows}
        rowKey={row => row.id}
        onRowClick={openRow}
        selectedKey={panelRow ? panelRow.id : null}
        loading={loading}
        stickyHeader
        rowLabel={row => `${row.display_name} · ${platformLabel(row.platform_name)}`}
        // Γραμμές με πολλές προτάσεις είναι ψηλές: τα κελιά ξεκινούν από πάνω, όχι στη μέση.
        rowClassName={() => '!items-start'}
        empty={(
          <EmptyState
            title="Καμία γραμμή με αυτά τα φίλτρα."
            hint="Πάτησε «Έλεγχος τώρα» για να διαβαστεί ξανά το ιστορικό παραγγελιών."
          />
        )}
        footer={pager}
        testId="mappings-table"
      />

      {/* ── Συρτάρι γραμμής: ό,τι ως 14/10 ζούσε μέσα στη γραμμή του πίνακα ── */}
      <DetailPanel
        open={panelRow !== null}
        onClose={closeRow}
        width="lg"
        title={panelRow ? panelRow.display_name : ''}
        subtitle={panelRow
          ? `${platformLabel(panelRow.platform_name)} · ${linesText(Number(panelRow.seen_count) || 0)} · τελ. τιμή ${fmtPrice(panelRow.last_platform_price)}`
          : undefined}
        footerStart={panelRow && canEdit ? (
          <>
            <button
              type="button"
              className={GHOST_BTN}
              disabled={panelBusy}
              onClick={() => ignoreRow(panelRow)}
              title="Δεν είναι προϊόν μας (π.χ. έξοδα, σχόλια) — να μην ξαναεμφανιστεί"
            >
              Αγνόησε
            </button>
            <button
              type="button"
              className={DANGER_GHOST_BTN}
              disabled={panelBusy}
              onClick={() => rejectRow(panelRow)}
              title="Οι προτάσεις είναι λάθος — να μείνει εκτός μέχρι να μπει σωστό προϊόν"
            >
              <Ban className="h-4 w-4" aria-hidden="true" /> Λάθος
            </button>
            {rowBusy && <Loader2 className="h-4 w-4 animate-spin text-t3" aria-hidden="true" />}
          </>
        ) : undefined}
        footer={<button type="button" className={GHOST_BTN} onClick={closeRow}>Κλείσιμο</button>}
        testId="mapping-panel"
      >
        {panelRow && (
          <div className="space-y-6">
            {panelNotice && noticeBanner(panelNotice)}

            {panelFlags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">{panelFlags}</div>
            )}

            <KeyValue items={detailItems(panelRow)} testId="mapping-details" />

            {/* Είδος γραμμής (spec §12.6): προϊόν, «ανά επιλογή» ή αγνόηση. Μπαίνει ΠΑΝΩ από τον
                στόχο γιατί αλλάζει το νόημα όσων ακολουθούν. */}
            {canEdit && (
              <SheetField label="Είδος" htmlFor={kindSelectId} hint={KIND_HELP[panelKind]}>
                <select
                  id={kindSelectId}
                  value={panelKind === 'bundle' ? 'product' : panelKind}
                  disabled={rowBusy}
                  onChange={e => changeKind(panelRow, e.target.value as MappingTargetKind)}
                  title={KIND_HELP[panelKind] || ''}
                  aria-describedby={KIND_HELP[panelKind] ? `${kindSelectId}-hint` : undefined}
                  className={`${SELECT_CLS} max-w-xs`}
                >
                  {KIND_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </SheetField>
            )}

            {panelByOption && (
              <ByOptionPanel
                row={panelRow}
                products={products}
                productsById={productsById}
                canEdit={canEdit}
                busy={panelBusy}
                onChange={updateRow}
              />
            )}

            <section className="space-y-3">
              {/* Ο στόχος μιας γραμμής «ανά επιλογή» είναι ΕΦΕΔΡΕΙΑ. */}
              <GroupTitle>{panelByOption ? 'Προϊόν αν δεν ταιριάξει καμία επιλογή' : 'Προϊόν καταλόγου'}</GroupTitle>
              <div className="text-sm"><TargetLine row={panelRow} /></div>

              {canEdit && hasCandidates(panelRow) && (
                <div className="space-y-2">
                  <p className="text-xs text-t3">Υποψήφια — ένα κλικ = αντιστοίχιση.</p>
                  <CandidateButtons
                    row={panelRow}
                    busy={rowBusy}
                    large
                    onPick={(pid, name) => applyTarget(panelRow, pid, name)}
                  />
                </div>
              )}

              {canEdit && (
                <div className="space-y-2">
                  <button type="button" onClick={() => openPicker(panelRow)} className={LINK_BTN}>
                    {pickerRowId === panelRow.id
                      ? 'Κλείσιμο'
                      : panelByOption
                        ? (panelTargetName ? 'Αλλαγή εφεδρικού προϊόντος…' : 'Επιλογή εφεδρικού προϊόντος…')
                        : (panelTargetName ? 'Αλλαγή προϊόντος…' : 'Επιλογή προϊόντος…')}
                  </button>
                  {pickerRowId === panelRow.id && (
                    <CatalogPicker
                      products={products}
                      disabled={rowBusy}
                      onPick={p => applyTarget(panelRow, p.id, p.name)}
                    />
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </DetailPanel>

      {/* ── Κάλυψη 7 ημερών (συρτάρι· το Esc το χειρίζεται το DetailPanel) ── */}
      <DetailPanel
        open={coverageOpen}
        onClose={() => setCoverageOpen(false)}
        width="lg"
        title="Κάλυψη τελευταίων 7 ημερών"
        subtitle="Ποσοστό γραμμών με αντιστοίχιση ανά κατάστημα και πλατφόρμα"
        footer={<button type="button" className={GHOST_BTN} onClick={() => setCoverageOpen(false)}>Κλείσιμο</button>}
        testId="coverage-panel"
      >
        <DataTable
          columns={coverageColumns}
          rows={coverageByStore}
          rowKey={g => g.storeId}
          ariaLabel="Κάλυψη ανά κατάστημα"
          empty="Δεν υπάρχουν στοιχεία κάλυψης για τις τελευταίες 7 ημέρες."
          testId="coverage-table"
        />
      </DetailPanel>

      <ConfirmModal
        isOpen={confirmAccept}
        variant="warning"
        title="Αποδοχή όλων των ακριβών"
        message={'Να εφαρμοστούν όλες οι εκκρεμείς γραμμές που έχουν ΑΚΡΙΒΩΣ ένα ταυτόσημο προϊόν και ΔΕΝ έχουν ρίσκο ΦΠΑ; '
          + `Οι γραμμές με ΦΠΑ διαφορετικό του ${DEFAULT_VAT}% μένουν εκκρεμείς για έλεγχο από άνθρωπο.`}
        confirmLabel="Εφαρμογή"
        cancelLabel="Άκυρο"
        onConfirm={acceptAllExact}
        onCancel={() => setConfirmAccept(false)}
      />
    </ListPage>
  );
};

export default OrgPlatformMappingsTab;
